import type { CellViewWire } from '@badger-sentinel/protocol';
import { BANDS } from '@badger-sentinel/rules';

import { WeatherGlyph, weatherColour } from './WeatherGlyph.js';
import { weatherFor } from '../model/selection.js';

/**
 * The environment block — weather now and next, debris per band (design 1180–1225).
 *
 * ## `forecast: null` is unknown (must-not #3)
 *
 * Three states, three renderings, and the middle one is the trap:
 *
 * - **`forecast` is null, or has no entry for this region** — a second row reading
 *   `UNKNOWN` with a `?` where the glyph goes. Never the current value repeated,
 *   never the row omitted; an absent row means "nothing changes" and that is a
 *   different claim.
 * - **`forecast` matches now** — no second row. Nothing changes, and saying so
 *   twice is the copy rule about saying a thing once.
 * - **`forecast` differs** — the next glyph, hollow, with its word and `T+1`.
 *
 * ## Debris
 *
 * Per band, current turn only, and there is no forecast for it on the wire — so
 * there is no next row and none is implied. The pips are an ordinal meter
 * against a fixed ceiling of five, which is the scale the rules use.
 */

const DEBRIS_MAX = 5;

export interface EnvironmentBlockProps {
  readonly view: CellViewWire;
  /** The region whose weather this is, or null for a subject with no ground under it. */
  readonly region: string | null;
  /** The band whose debris this is, or null. */
  readonly band: string | null;
}

export function EnvironmentBlock(props: EnvironmentBlockProps): JSX.Element | null {
  const weather = props.region === null ? null : weatherFor(props.view, props.region);
  const debris =
    props.band === null
      ? null
      : ((props.view.theatre.debris as Readonly<Record<string, number>>)[props.band] ?? null);

  if (weather === null && debris === null) return null;

  return (
    <div
      data-testid="environment-block"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '10px 14px 11px',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...LABEL, flex: 'none' }}>ENVIRONMENT</span>
        <span style={{ flex: 1 }} />
        <span
          data-testid="environment-zone"
          style={{ ...LABEL, flex: 'none', color: 'var(--text-3)' }}
        >
          {props.region ?? props.band ?? ''}
        </span>
      </div>

      {weather !== null && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ ...LABEL, flex: 'none', width: 56 }}>WEATHER</span>
            <WeatherGlyph state={weather.now} outlook="now" size={21} />
            <span
              data-testid="weather-now"
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '.10em',
                color: weatherColour(weather.now),
              }}
            >
              {weather.now ?? 'UNKNOWN'}
            </span>
            <span style={{ ...LABEL, flex: 'none' }}>NOW</span>
          </div>

          {weather.next !== null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span style={{ flex: 'none', width: 56 }} />
              <WeatherGlyph
                state={weather.next.known ? weather.next.state : null}
                outlook="next"
                size={19}
              />
              <span
                data-testid="weather-next"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: 'var(--font-data)',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '.10em',
                  color: weather.next.known ? weatherColour(weather.next.state) : 'var(--text-dim)',
                }}
              >
                {weather.next.known ? weather.next.state : 'UNKNOWN'}
              </span>
              <span style={{ ...LABEL, flex: 'none', color: 'var(--primary)' }}>T+1</span>
            </div>
          )}
        </div>
      )}

      {debris !== null && props.band !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ ...LABEL, flex: 'none', width: 56 }}>DEBRIS</span>
          <span
            data-testid="debris-level"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 13,
              fontWeight: 600,
              color:
                debris >= 4 ? 'var(--red-text)' : debris >= 2 ? 'var(--warn)' : 'var(--text-2)',
            }}
          >
            {debris}
          </span>
          <span aria-hidden="true" style={{ flex: 'none', display: 'flex', gap: 2 }}>
            {Array.from({ length: DEBRIS_MAX }, (_unused, index) => (
              <span
                key={index}
                style={{
                  width: 5,
                  height: 13,
                  boxSizing: 'border-box',
                  border: '1px solid var(--border)',
                  background: index < debris ? 'var(--warn)' : 'transparent',
                }}
              />
            ))}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ ...LABEL, flex: 'none' }}>{props.band}</span>
        </div>
      )}
    </div>
  );
}

/** The band a location sits in, or null for anything on the ground. */
export function bandOf(band: string | null): string | null {
  return band !== null && (BANDS as readonly string[]).includes(band) ? band : null;
}

const LABEL = {
  fontFamily: 'var(--font-data)',
  fontSize: 11,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
} as const;
